import { styled } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

import { OpenInNewIcon } from '../../../icons';
import type { ResourceColorPalette } from '../../../theme/tokens/resourcesColors';
import { Tag } from '../../atoms/Tag';
import { useCardResource } from '../CardResource';
import {
  DOCUMENT_LIST_WIDE_QUERY,
  DOCUMENT_TILE_SIZE,
  DOCUMENT_TILE_SIZE_WIDE,
} from '../DocumentList/documentListContainer';

const DEFAULT_NEW_TAB_LABEL = '(s’ouvre dans un nouvel onglet)';

type Props = {
  name: string;
  /** Address of the file. The name opens it in a new tab. */
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** Nature of the document, e.g. "Facture", "Contrat". */
  type?: string | null;
  /** Linked provider, typically a `ProviderLink`. Rendered above the row link so it stays its own target. */
  provider?: ReactNode;
  /** Displayed date, already formatted (e.g. "14 oct. 2026"). */
  date?: string;
  /** Machine-readable date (ISO 8601) for the `<time>` element. */
  dateTime?: string;
  /** Preview image (photos). Falls back to the documents tile. */
  thumbnailUrl?: string | null;
  /** Secondary controls (resource menu). Rendered above the row link, never nested in it. */
  action?: ReactNode;
  /** Announced after the name to warn that the link leaves the app. */
  newTabLabel?: string;
  'data-testid'?: string;
};

type OwnerState = {
  resourceColor: ResourceColorPalette;
};

const Row = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `${DOCUMENT_TILE_SIZE}px minmax(0, 1fr) auto`,
  gridTemplateAreas: `
    "tile name trailing"
    "tile meta trailing"
    "tile provider trailing"`,
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  alignItems: 'center',
  minHeight: theme.spacing(9),
  paddingBlock: theme.spacing(1.5),
  paddingInline: theme.spacing(2),
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:has(a:focus-visible)': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  [DOCUMENT_LIST_WIDE_QUERY]: {
    gridTemplateColumns: `${DOCUMENT_TILE_SIZE_WIDE}px minmax(12rem, 2fr) minmax(0, 1fr) minmax(0, 1fr) 7rem auto`,
    gridTemplateAreas: '"tile name type provider date trailing"',
    columnGap: theme.spacing(2),
    minHeight: theme.spacing(7),
    paddingBlock: theme.spacing(1),
  },
}));

const Tile = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  gridArea: 'tile',
  alignSelf: 'start',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: DOCUMENT_TILE_SIZE,
  height: DOCUMENT_TILE_SIZE,
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: ownerState.resourceColor.light,
  color: ownerState.resourceColor.main,
  [DOCUMENT_LIST_WIDE_QUERY]: {
    alignSelf: 'center',
    width: DOCUMENT_TILE_SIZE_WIDE,
    height: DOCUMENT_TILE_SIZE_WIDE,
  },
}));

const Thumbnail = styled('img')({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const NameLink = styled('a')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  gridArea: 'name',
  minWidth: 0,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  overflowWrap: 'anywhere',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
  },
  '&:focus-visible': {
    outline: 'none',
  },
  [DOCUMENT_LIST_WIDE_QUERY]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflowWrap: 'normal',
  },
}));

const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

const Meta = styled('span')(({ theme }) => ({
  gridArea: 'meta',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.5),
  minWidth: 0,
  [DOCUMENT_LIST_WIDE_QUERY]: {
    display: 'contents',
  },
}));

const TypeCell = styled('span')({
  gridArea: 'type',
  display: 'flex',
  minWidth: 0,
});

const ProviderCell = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  gridArea: 'provider',
  position: 'relative',
  zIndex: 1,
  minWidth: 0,
  overflow: 'hidden',
  color: theme.palette.text.secondary,
}));

const DateText = styled('time')(({ theme }) => ({
  ...theme.typography.caption,
  gridArea: 'date',
  color: theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  [DOCUMENT_LIST_WIDE_QUERY]: {
    ...theme.typography.body2,
    textAlign: 'right',
  },
}));

const Trailing = styled('span')(({ theme }) => ({
  gridArea: 'trailing',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

const ActionSlot = styled('span')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  marginRight: theme.spacing(-1),
}));

/**
 * One file of the space library, inside a `DocumentList`.
 * The name is the row link and opens the file in a new tab (its hit area covers the whole row);
 * the provider link and the optional action sit above it so they stay separate targets.
 */
export function DocumentListItem(props: Props) {
  const { resourceColor, IconComponent } = useCardResource({ variant: 'documents' });
  const hasMeta = Boolean(props.type || props.date);

  return (
    <li data-testid={props['data-testid'] ?? 'document-list-item'}>
      <Row>
        <Tile aria-hidden="true" ownerState={{ resourceColor }}>
          {props.thumbnailUrl ? (
            <Thumbnail src={props.thumbnailUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
          ) : IconComponent ? (
            <IconComponent fontSize="small" />
          ) : null}
        </Tile>
        <NameLink href={props.href} target="_blank" rel="noopener noreferrer" onClick={props.onClick}>
          {props.name}
          <VisuallyHidden> {props.newTabLabel ?? DEFAULT_NEW_TAB_LABEL}</VisuallyHidden>
        </NameLink>
        {hasMeta ? (
          <Meta>
            {props.type ? (
              <TypeCell>
                <Tag label={props.type} color="default" />
              </TypeCell>
            ) : null}
            {props.date ? <DateText dateTime={props.dateTime}>{props.date}</DateText> : null}
          </Meta>
        ) : null}
        {props.provider ? <ProviderCell>{props.provider}</ProviderCell> : null}
        <Trailing>
          {props.action ? (
            <ActionSlot>{props.action}</ActionSlot>
          ) : (
            <OpenInNewIcon fontSize="small" aria-hidden="true" />
          )}
        </Trailing>
      </Row>
    </li>
  );
}

export type { Props as DocumentListItemProps };
